/**
 * The announcements this browser already put on screen, per signed-in user,
 * so a popup never shows twice even when its seen call failed (the next check
 * sends that call again). The server only serves the days around today, so an
 * id older than a few days can never come back and is pruned. The mobile
 * clients keep the same memory.
 */

const SHOWN_KEY = 'app.announcementsShown'
const KEEP_MS = 4 * 24 * 60 * 60 * 1000

/** "<userId>:<announcementId>" -> when it was shown (epoch ms). */
type ShownMap = Record<string, number>

function entryKey(userId: string, id: string): string {
  return `${userId.toLowerCase()}:${id.toLowerCase()}`
}

function readShown(): ShownMap {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(SHOWN_KEY) ?? '{}')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as ShownMap)
      : {}
  } catch {
    return {}
  }
}

/** Whether this user already saw announcement `id` in this browser. */
export function wasAnnouncementShown(userId: string, id: string): boolean {
  return entryKey(userId, id) in readShown()
}

/** Remembers that `id` is on screen for this user (and drops expired entries). */
export function rememberAnnouncementShown(userId: string, id: string): void {
  const now = Date.now()
  const kept: ShownMap = {}
  for (const [key, shownAt] of Object.entries(readShown())) {
    if (typeof shownAt === 'number' && now - shownAt < KEEP_MS) kept[key] = shownAt
  }
  kept[entryKey(userId, id)] = now
  try {
    localStorage.setItem(SHOWN_KEY, JSON.stringify(kept))
  } catch {
    // Storage disabled: the server's seen record still keeps it from returning.
  }
}

/** The device's local calendar day as yyyy-MM-dd, the day the server filters on. */
export function localDateKey(date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}
